import React from 'react';
import { Modal, Box, Typography, CircularProgress } from '@mui/material';
import styled from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';

const ModalContainer = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(440px, 90%);
  background: #fff;
  border-radius: 32px;
  border: 8px solid #d1d5db;
  padding: clamp(24px, 4vw, 40px) clamp(24px, 5vw, 40px);
  text-align: center;
  backdrop-filter: blur(25.448572158813477px);
  font-family: 'Urbanist';
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  position: relative;

  @media (max-width: 480px) {
    width: 95%;
    max-height: 90vh;
    overflow-y: auto;
    border-radius: 24px;
    border: 6px solid #d1d5db;
  }
  
  @media (max-width: 360px) {
    border: 4px solid #d1d5db;
    border-radius: 20px;
  }
`;

const IconContainer = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  margin: 0 auto clamp(24px, 4vw, 32px) auto;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 480px) {
    width: 80px;
    height: 80px;
    margin-bottom: 20px;
  }
`;

const Title = styled(Typography)`
  font-size: clamp(20px, 3vw, 24px) !important;
  font-weight: 600 !important;
  color: #1a1a1a !important;
  margin-bottom: clamp(16px, 2vw, 20px) !important;
  font-family: var(--font-clash-display) !important;
  
  @media (max-width: 480px) {
    font-size: 18px !important;
  }
`;

const Description = styled(Typography)`
  font-size: clamp(14px, 2vw, 16px) !important;
  color: #181C23 !important;
  margin-bottom: clamp(28px, 4vw, 36px) !important;
  font-family: 'Urbanist', -apple-system, BlinkMacSystemFont, sans-serif !important;
  line-height: 1.5 !important;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 480px) {
    max-width: 100%;
    margin-bottom: 24px !important;
  }
`;

const DeleteButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  background: #BF0A00;
  color: white;
  border: none;
  border-radius: 25px;
  font-family: 'Urbanist', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  box-shadow: 0 4px 16px rgba(220, 38, 38, 0.3);
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(220, 38, 38, 0.4);
    background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    box-shadow: 0 4px 16px rgba(220, 38, 38, 0.2);
  }
  
  @media (max-width: 480px) {
    max-width: 100%;
    padding: 12px 20px;
    font-size: 15px;
    min-height: 44px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: #999999;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  z-index: 10;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    color: #666666;
  }
  
  @media (max-width: 480px) {
    top: 16px;
    right: 16px;
    padding: 6px;
  }
`;

const LoaderSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @media (max-width: 480px) {
    width: 18px;
    height: 18px;
    margin-right: 6px;
  }
`;

const ErrorMessage = styled(Typography)`
  color: #dc2626 !important;
  font-size: 14px !important;
  margin-top: 16px !important;
  font-family: 'Urbanist' !important;
  
  @media (max-width: 480px) {
    font-size: 13px !important;
    margin-top: 12px !important;
  }
`;

const DeleteModal = ({
  open,
  onClose,
  onDelete,
  isDeleting = false,
  error,
  title = "Are You Sure You Want to Delete This Recommendation?",
  description = "This action will permanently remove the recommendation from the boss's profile and cannot be undone.",
  deleteButtonText = "Delete Recommendation",
  children
}) => {
  return (
    <Modal
      open={open}
      onClose={!isDeleting ? onClose : undefined}
      aria-labelledby="delete-modal-title"
      aria-describedby="delete-modal-description"
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.4)'
        }
      }}
    >
      <ModalContainer>
        <CloseButton onClick={onClose} disabled={isDeleting}>
          <CloseIcon fontSize="small" />
        </CloseButton>

        <IconContainer>
          <svg xmlns="http://www.w3.org/2000/svg" width="158" height="144" viewBox="0 0 158 144" fill="none">
            <rect opacity="0.5" x="14.3691" y="0.5" width="51.5427" height="51.5427" rx="10" fill="#9499FF" />
            <rect opacity="0.5" x="128.32" y="33.3672" width="29.3594" height="29.3594" rx="10" fill="#3A43FF" />
            <rect opacity="0.5" x="0.320312" y="75.3672" width="31.5329" height="31.5329" rx="8" fill="#898FFF" />
            <rect opacity="0.5" x="114.607" y="107.32" width="36.5935" height="36.5935" rx="8" fill="url(#paint0_linear_802_3707)" />
            <circle cx="83.7969" cy="78.875" r="60" fill="#12188A" />
            <path d="M65.4629 59L102.289 98.7333" stroke="white" stroke-width="6.57608" stroke-linecap="round" />
            <path d="M102.289 59L65.463 98.7333" stroke="white" stroke-width="6.57608" stroke-linecap="round" />
            <defs>
              <linearGradient id="paint0_linear_802_3707" x1="106.82" y1="107.499" x2="153.32" y2="145.999" gradientUnits="userSpaceOnUse">
                <stop stop-color="#12188A" />
                <stop offset="1" stop-color="#222CF3" stop-opacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </IconContainer>

        {children ? (
          children
        ) : (
          <>
            <Title variant="h5">
              {title}
            </Title>

            <Description>
              {description}
            </Description>
          </>
        )}

        <DeleteButton
          onClick={onDelete}
          disabled={isDeleting}
        >
          {isDeleting && <LoaderSpinner />}
          {deleteButtonText}
        </DeleteButton>

        {error && (
          <ErrorMessage>
            {error}
          </ErrorMessage>
        )}
      </ModalContainer>
    </Modal>
  );
};

export default DeleteModal;